import React, { useState, useEffect, useCallback } from "react";
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  MapPinIcon,
  FunnelIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../../services/api";
import { Alert } from "../../types";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import InputDialog from "../../components/common/InputDialog";

export default function AlertsPage() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [alertToResolve, setAlertToResolve] = useState<string | null>(null);

  const itemsPerPage = 10;

  const loadAlerts = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        status: statusFilter || undefined,
        severity: severityFilter || undefined,
        type: typeFilter || undefined,
        page: currentPage,
        limit: itemsPerPage,
      };

      const response = await apiService.getAlerts(params);

      // Defensive handling for different response structures
      // Expected: { success: true, data: { alerts: [], pagination: {} } }
      // Or potentially: { alerts: [], pagination: {} }

      let alertsData: Alert[] = [];
      let paginationData = { total: 0, total_pages: 1 };

      if (response.data && response.data.alerts) {
        alertsData = response.data.alerts;
        paginationData = response.data.pagination;
      } else if ((response as any).alerts) {
        alertsData = (response as any).alerts;
        paginationData = (response as any).pagination || paginationData;
      } else if (Array.isArray(response)) {
        alertsData = response;
      }

      setAlerts(alertsData);
      setTotalAlerts(paginationData?.total || alertsData.length);
      setTotalPages(paginationData?.total_pages || 1);
    } catch (err: any) {
      setError("Erro ao carregar alertas");
      console.error("Alerts error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, severityFilter, typeFilter, currentPage, itemsPerPage]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const handleAcknowledgeAlert = async (alertId: string) => {
    try {
      await apiService.acknowledgeAlert(alertId);
      loadAlerts(); // Reload to get updated data
    } catch (err) {
      console.error("Error acknowledging alert:", err);
    }
  };

  const handleResolveAlert = (alertId: string) => {
    setAlertToResolve(alertId);
    setResolveDialogOpen(true);
  };

  const confirmResolveAlert = async (resolution: string) => {
    if (!alertToResolve) return;

    try {
      await apiService.resolveAlert(alertToResolve, resolution);
      loadAlerts(); // Reload to get updated data
      setResolveDialogOpen(false);
      setAlertToResolve(null);
    } catch (err) {
      console.error("Error resolving alert:", err);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "text-red-600 bg-red-100 border-red-200";
      case "high":
        return "text-orange-600 bg-orange-100 border-orange-200";
      case "medium":
        return "text-yellow-600 bg-yellow-100 border-yellow-200";
      case "low":
        return "text-blue-600 bg-blue-100 border-blue-200";
      default:
        return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "open":
        return "text-red-600 bg-red-100";
      case "acknowledged":
        return "text-yellow-600 bg-yellow-100";
      case "resolved":
        return "text-green-600 bg-green-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getAlertTypeIcon = (type: string) => {
    switch (type) {
      case "hardware_change":
        return <ComputerDesktopIcon className="w-5 h-5" />;
      case "blocked_site":
        return <GlobeAltIcon className="w-5 h-5" />;
      case "offline":
        return <XCircleIcon className="w-5 h-5" />;
      case "location_change":
        return <MapPinIcon className="w-5 h-5" />;
      default:
        return <ExclamationTriangleIcon className="w-5 h-5" />;
    }
  };

  const getAlertTypeName = (type: string) => {
    switch (type) {
      case "hardware_change":
        return "Mudança de Hardware";
      case "blocked_site":
        return "Site Bloqueado";
      case "offline":
        return "Máquina Offline";
      case "location_change":
        return "Mudança de Localização";
      default:
        return type;
    }
  };

  const translateStatus = (status: string) => {
    switch (status) {
      case "open":
        return "Aberto";
      case "acknowledged":
        return "Confirmado";
      case "resolved":
        return "Resolvido";
      default:
        return status;
    }
  };

  const translateSeverity = (severity: string) => {
    switch (severity) {
      case "critical":
        return "Crítico";
      case "high":
        return "Alto";
      case "medium":
        return "Médio";
      case "low":
        return "Baixo";
      default:
        return severity;
    }
  };

  const clearFilters = () => {
    setStatusFilter("");
    setSeverityFilter("");
    setTypeFilter("");
    setCurrentPage(1);
  };

  const openAlertDetails = (alert: Alert) => {
    setSelectedAlert(alert);
    setShowDetails(true);
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-700">{error}</div>
        <button
          onClick={loadAlerts}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Alertas
        </h1>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {totalAlerts} alerta{totalAlerts !== 1 ? "s" : ""} encontrado
          {totalAlerts !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Status
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Todos</option>
              <option value="open">Aberto</option>
              <option value="acknowledged">Confirmado</option>
              <option value="resolved">Resolvido</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="severity"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Severidade
            </label>
            <select
              id="severity"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Todas</option>
              <option value="critical">Crítica</option>
              <option value="high">Alta</option>
              <option value="medium">Média</option>
              <option value="low">Baixa</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="type"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Tipo
            </label>
            <select
              id="type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Todos</option>
              <option value="hardware_change">Mudança de Hardware</option>
              <option value="blocked_site">Site Bloqueado</option>
              <option value="offline">Máquina Offline</option>
              <option value="location_change">Mudança de Localização</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FunnelIcon className="w-4 h-4 mr-2" />
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Alerts List */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              Nenhum alerta encontrado
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Tente ajustar os filtros ou aguarde novos alertas.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {alerts.map((alert) => (
              <li key={alert.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div
                      className={`p-2 rounded-full ${getSeverityColor(
                        alert.severity
                      )}`}
                    >
                      {getAlertTypeIcon(alert.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {alert.title}
                        </p>
                        {(alert.machine_name || alert.machine_branch) && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            • {alert.machine_name || "Máquina desconhecida"}
                            {alert.machine_branch &&
                              ` - ${alert.machine_branch}`}
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(
                            alert.severity
                          )}`}
                        >
                          {translateSeverity(alert.severity)}
                        </span>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            alert.status
                          )}`}
                        >
                          {translateStatus(alert.status)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-1">
                        {alert.description}
                      </p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-400">
                        <span>{getAlertTypeName(alert.type)}</span>
                        <span>•</span>
                        <span>
                          {new Date(alert.created_at).toLocaleString("pt-BR")}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openAlertDetails(alert)}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      <EyeIcon className="w-3 h-3 mr-1" />
                      Ver
                    </button>
                    {alert.status === "open" && (
                      <button
                        onClick={() => handleAcknowledgeAlert(alert.id)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-yellow-600 hover:bg-yellow-700"
                      >
                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                        Confirmar
                      </button>
                    )}
                    {(alert.status === "open" ||
                      alert.status === "acknowledged") && (
                      <button
                        onClick={() => handleResolveAlert(alert.id)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                        Resolver
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próximo
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Mostrando{" "}
                <span className="font-medium">
                  {(currentPage - 1) * itemsPerPage + 1}
                </span>{" "}
                até{" "}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, totalAlerts)}
                </span>{" "}
                de <span className="font-medium">{totalAlerts}</span> resultados
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === page
                          ? "z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400"
                          : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próximo
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Alert Details Modal */}
      {showDetails && selectedAlert && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800 dark:border-gray-700">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Detalhes do Alerta
                </h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Título
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {selectedAlert.title}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Descrição
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {selectedAlert.description}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Tipo
                    </label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {getAlertTypeName(selectedAlert.type)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Severidade
                    </label>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(
                        selectedAlert.severity
                      )}`}
                    >
                      {translateSeverity(selectedAlert.severity)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Status
                    </label>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        selectedAlert.status
                      )}`}
                    >
                      {translateStatus(selectedAlert.status)}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Criado em
                    </label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {new Date(selectedAlert.created_at).toLocaleString(
                        "pt-BR"
                      )}
                    </p>
                  </div>
                </div>

                {selectedAlert.status === "resolved" &&
                  selectedAlert.resolution && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
                      <label className="block text-sm font-medium text-green-800 dark:text-green-300 mb-2">
                        Resolução
                      </label>
                      <p className="text-sm text-green-900 dark:text-green-100">
                        {selectedAlert.resolution}
                      </p>
                      {selectedAlert.resolved_at && (
                        <p className="text-xs text-green-700 dark:text-green-400 mt-2">
                          Resolvido em:{" "}
                          {new Date(selectedAlert.resolved_at).toLocaleString(
                            "pt-BR"
                          )}
                        </p>
                      )}
                    </div>
                  )}

                {selectedAlert.data && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Dados Adicionais
                    </label>
                    <pre className="mt-1 text-xs bg-gray-50 dark:bg-gray-900 p-3 rounded-md overflow-x-auto dark:text-gray-300">
                      {JSON.stringify(selectedAlert.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                {selectedAlert.status === "open" && (
                  <button
                    onClick={() => {
                      handleAcknowledgeAlert(selectedAlert.id);
                      setShowDetails(false);
                    }}
                    className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700"
                  >
                    Confirmar
                  </button>
                )}
                {(selectedAlert.status === "open" ||
                  selectedAlert.status === "acknowledged") && (
                  <button
                    onClick={() => {
                      handleResolveAlert(selectedAlert.id);
                      setShowDetails(false);
                    }}
                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
                  >
                    Resolver
                  </button>
                )}
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-md hover:bg-gray-400 dark:hover:bg-gray-500"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <InputDialog
        isOpen={resolveDialogOpen}
        title="Resolver Alerta"
        message="Por favor, descreva a resolução do alerta:"
        placeholder="Ex: Problema resolvido, componente substituído, etc."
        defaultValue="Resolvido manualmente"
        confirmText="Resolver"
        cancelText="Cancelar"
        type="info"
        onConfirm={confirmResolveAlert}
        onCancel={() => {
          setResolveDialogOpen(false);
          setAlertToResolve(null);
        }}
      />
    </div>
  );
}
